import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { ModuleCatalogTable } from "./-components/module-catalog-table";
import { ModuleForm, type ModuleFormValues } from "./-components/module-form";
import {
  PlanCatalogTable,
  type PlanModule,
  type PricingPlan,
} from "./-components/plan-catalog-table";
import { PlanForm, type PlanFormValues } from "./-components/plan-form";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/plans-modules/catalog",
)({
  component: CatalogPage,
});

type Editing = { mode: "create" } | { mode: "edit"; row: PlanModule };
type EditingPlan = { mode: "create" } | { mode: "edit"; row: PricingPlan };

/** One shape for both modes, so the form's type is stated once. */
function planDefaults(plan: PricingPlan | null): PlanFormValues {
  if (!plan) return blankPlan;
  return {
    key: plan.key,
    name: plan.name,
    description: plan.description ?? "",
    tier: plan.tier,
    isFree: plan.isFree,
    // The row comes back as a plain string; the form takes the two the
    // schema allows, and anything else is a plan priced by something this
    // build does not know how to charge for.
    pricingBasis: plan.pricingBasis === "per_census" ? "per_census" : "per_bed",
    pricePerUnit: Number(plan.pricePerUnit),
    baseMonthlyFee: Number(plan.baseMonthlyFee),
    isActive: plan.isActive,
    sortOrder: plan.sortOrder,
    moduleIds: [...plan.moduleIds],
  };
}

const blankPlan: PlanFormValues = {
  key: "",
  name: "",
  description: "",
  tier: 0,
  isFree: false,
  pricingBasis: "per_bed",
  pricePerUnit: 0,
  baseMonthlyFee: 0,
  isActive: true,
  sortOrder: 0,
  moduleIds: [],
};

const blank: ModuleFormValues = {
  key: "",
  name: "",
  description: "",
  isMedicaidOnly: false,
  featureKeys: [],
  sortOrder: 0,
};

/** Both listings are the platform's own: `public.plans` shows only active plans, so a retired tier
 * a tenant is on, or a module on no plan, is invisible elsewhere. */
function CatalogPage() {
  const canManage = useCan("platform:manage");
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editingPlan, setEditingPlan] = useState<EditingPlan | null>(null);
  const queryClient = useQueryClient();
  const plans = useQuery(orpc.administration.plans.list.queryOptions());
  const modules = useQuery(orpc.administration.plans.modules.queryOptions());

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.plans.key() });
  const create = useMutation(
    orpc.administration.plans.createModule.mutationOptions({
      onSuccess: async () => {
        toast.success("Module added");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const createPlan = useMutation(
    orpc.administration.plans.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Plan added");
        setEditingPlan(null);
        await invalidate();
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  /* Two calls on purpose: the fields and the module list are separate writes on the server, and the
   * second is the one that changes what a tenant can open. Failing halfway leaves the price changed
   * and the plan intact, which is the safer half to lose. */
  const updatePlan = useMutation(
    orpc.administration.plans.update.mutationOptions({
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const setPlanModules = useMutation(
    orpc.administration.plans.setModules.mutationOptions({
      onSuccess: async () => {
        toast.success("Plan saved");
        setEditingPlan(null);
        await invalidate();
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const update = useMutation(
    orpc.administration.plans.updateModule.mutationOptions({
      onSuccess: async () => {
        toast.success("Module updated");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const submit = (values: ModuleFormValues) => {
    // An emptied description is no description, not an empty one.
    const module = { ...values, description: values.description?.trim() || null };
    if (editing?.mode === "edit") {
      update.mutate({ ...module, moduleId: editing.row.id });
      return;
    }
    create.mutate(module);
  };

  const header = (
    <PageHeader
      title="Plan and module catalog"
      meta={
        <>
          <Link to="/settings/plans-modules" className="hover:underline">
            All organizations
          </Link>
          {plans.data && modules.data
            ? `, ${plans.data.length} plans, ${modules.data.length} modules`
            : null}
        </>
      }
      actions={
        canManage ? (
          <span className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditingPlan({ mode: "create" })}>
              Add plan
            </Button>
            <Button onClick={() => setEditing({ mode: "create" })}>Add module</Button>
          </span>
        ) : null
      }
    />
  );

  if (plans.isPending || modules.isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-96 w-full" />
      </>
    );
  }
  if (plans.isError || modules.isError) {
    return (
      <>
        {header}
        <ReadFailed
          message="Could not load the catalog."
          error={plans.error ?? modules.error}
          retrying={plans.isFetching || modules.isFetching}
          onRetry={() => {
            void plans.refetch();
            void modules.refetch();
          }}
        />
      </>
    );
  }

  return (
    <>
      {header}

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Plans</h3>
        <PlanCatalogTable
          plans={plans.data}
          modules={modules.data}
          canManage={canManage}
          onEdit={(row) => setEditingPlan({ mode: "edit", row })}
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium">Modules</h3>
        <ModuleCatalogTable
          modules={modules.data}
          plans={plans.data}
          canManage={canManage}
          onEdit={(row) => setEditing({ mode: "edit", row })}
        />
      </section>

      <Dialog
        open={editingPlan !== null}
        onOpenChange={(open) => (open ? null : setEditingPlan(null))}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan?.mode === "edit" ? `Edit ${editingPlan.row.name}` : "Add plan"}
            </DialogTitle>
          </DialogHeader>
          {editingPlan ? (
            <PlanForm
              editing={editingPlan.mode === "edit"}
              modules={modules.data}
              pending={createPlan.isPending || updatePlan.isPending || setPlanModules.isPending}
              defaults={planDefaults(editingPlan.mode === "edit" ? editingPlan.row : null)}
              onCancel={() => setEditingPlan(null)}
              onSubmit={(values) => {
                if (editingPlan.mode === "create") {
                  createPlan.mutate(values);
                  return;
                }
                const planId = editingPlan.row.id;
                const { key: _key, moduleIds, ...fields } = values;
                updatePlan.mutate(
                  { planId, ...fields },
                  {
                    onSuccess: () => setPlanModules.mutate({ planId, moduleIds: moduleIds ?? [] }),
                  },
                );
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === "edit" ? `Edit ${editing.row.name}` : "Add module"}
            </DialogTitle>
          </DialogHeader>
          {editing ? (
            <ModuleForm
              editing={editing.mode === "edit"}
              pending={create.isPending || update.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={submit}
              defaults={
                editing.mode === "edit"
                  ? {
                      key: editing.row.key,
                      name: editing.row.name,
                      description: editing.row.description ?? "",
                      isMedicaidOnly: editing.row.isMedicaidOnly,
                      featureKeys: editing.row.featureKeys,
                      sortOrder: editing.row.sortOrder,
                    }
                  : blank
              }
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
